import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X } from "lucide-react";
import { api, fileUrl, eur, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ALL_SIZES = ["S", "M", "L", "XL", "XXL"];
const emptyForm = {
  name: "",
  partner: "",
  collab_type: "automobile",
  category: "jersey",
  price: "",
  description: "",
  sizes: ["S", "M", "L", "XL", "XXL"],
  images: [],
  thumbnail: null,
  featured: false,
};

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user === false) navigate("/auth?next=/admin");
    else if (user && user.role !== "admin") navigate("/");
    else if (user) load();
  }, [user, navigate]);

  if (!user || user.role !== "admin") return <div className="min-h-screen" />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleSize = (s) =>
    setForm((f) => ({
      ...f,
      sizes: f.sizes.includes(s) ? f.sizes.filter((x) => x !== s) : [...f.sizes, s],
    }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, images: [...f.images, data.path] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseFloat(form.price) };
    try {
      if (editing) {
        await api.put(`/admin/products/${editing}`, payload);
        toast.success("Piece updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Piece added to the vault");
      }
      setForm(emptyForm);
      setEditing(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
    }
  };

  const startEdit = (p) => {
    setForm({ ...p, price: String(p.price) });
    setEditing(p.id);
    setShowForm(true);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Piece removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed"));
    }
  };

  const inputCls =
    "w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-[#F5F5F5] placeholder:text-zinc-600 focus:border-gold focus:outline-none transition-colors duration-300";

  return (
    <div data-testid="admin-page" className="min-h-screen pt-32 md:pt-40 pb-28">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <p className="text-xs tracking-[0.35em] uppercase text-gold">Atelier</p>
            <h1 className="mt-3 font-serif text-4xl md:text-5xl font-light text-[#F5F5F5]">
              Inventory Control
            </h1>
          </div>
          <button
            data-testid="admin-new-product-button"
            onClick={() => {
              setForm(emptyForm);
              setEditing(null);
              setShowForm(!showForm);
            }}
            className="flex items-center gap-2 px-8 py-3 bg-gold text-black text-xs tracking-[0.3em] uppercase font-semibold hover:bg-gold-hover transition-colors duration-500"
          >
            {showForm ? <X size={13} /> : <Plus size={13} />}
            {showForm ? "Close" : "New Piece"}
          </button>
        </div>

        <div className="mt-10 flex gap-8 border-b border-white/10">
          {["products", "orders"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`pb-4 text-xs tracking-[0.3em] uppercase border-b transition-colors duration-300 ${
                tab === t ? "text-gold border-gold" : "text-zinc-500 border-transparent hover:text-zinc-200"
              }`}
            >
              {t === "products" ? `Pieces (${products.length})` : `Orders (${orders.length})`}
            </button>
          ))}
        </div>

        {showForm && (
          <form
            onSubmit={submit}
            data-testid="admin-product-form"
            className="mt-10 border border-white/10 bg-[#141414] p-8 grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            <input data-testid="admin-form-name" placeholder="Piece name (e.g. Conqueror Home Jersey)" value={form.name} onChange={(e) => set("name", e.target.value)} required className={inputCls} />
            <input data-testid="admin-form-partner" placeholder="Collaboration (e.g. Madrid × Bugatti)" value={form.partner} onChange={(e) => set("partner", e.target.value)} className={inputCls} />
            <select data-testid="admin-form-collab" value={form.collab_type} onChange={(e) => set("collab_type", e.target.value)} className={inputCls}>
              <option value="automobile">Football × Automobile</option>
              <option value="cinema">Football × Cinema</option>
              <option value="wilden">Football × Wilden</option>
              <option value="varsity">Varsity (inside Pebble The Otter)</option>
              <option value="otter">Pebble The Otter</option>
              <option value="originals">Vincen Originals</option>
              <option value="plus">Vincen Plus</option>
            </select>
            <select data-testid="admin-form-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
              <option value="jersey">Jersey</option>
              <option value="hoodie">Hoodie</option>
              <option value="sweatshirt">Sweatshirt</option>
              <option value="polo">Polo</option>
              <option value="zipper">Zipper</option>
            </select>
            <input data-testid="admin-form-price" type="number" step="0.01" min="0" placeholder="Price (EUR)" value={form.price} onChange={(e) => set("price", e.target.value)} required className={inputCls} />
            <div className="flex items-center gap-3">
              <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Sizes</label>
              <div className="flex gap-2 flex-wrap">
                {ALL_SIZES.map((s) => (
                  <button
                    type="button"
                    key={s}
                    data-testid={`admin-form-size-${s}`}
                    onClick={() => toggleSize(s)}
                    className={`px-3 py-2 border text-xs transition-colors duration-300 ${
                      form.sizes.includes(s) ? "border-gold text-gold" : "border-white/15 text-zinc-500"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <textarea data-testid="admin-form-description" placeholder="Description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className={`${inputCls} md:col-span-2`} />

            <div className="md:col-span-2">
              <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">Images</p>
              <div className="flex gap-3 flex-wrap items-center">
                {form.images.map((img, i) => {
                  const isCover = (form.thumbnail || form.images[0]) === img;
                  return (
                    <div key={i} className={`relative w-20 h-24 border overflow-hidden group ${isCover ? "border-gold ring-1 ring-gold/40" : "border-white/10"}`}>
                      <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        data-testid={`admin-remove-image-${i}`}
                        onClick={() => {
                          const nextImgs = form.images.filter((_, x) => x !== i);
                          setForm((f) => ({ ...f, images: nextImgs, thumbnail: f.thumbnail === img ? null : f.thumbnail }));
                        }}
                        className="absolute top-1 right-1 bg-black/70 p-1 text-zinc-400 hover:text-red-400"
                      >
                        <X size={11} />
                      </button>
                      <button
                        type="button"
                        data-testid={`admin-set-cover-${i}`}
                        onClick={() => set("thumbnail", img)}
                        className={`absolute bottom-0 inset-x-0 py-1 text-[8px] tracking-[0.2em] uppercase ${isCover ? "bg-gold text-black" : "bg-black/70 text-zinc-300 opacity-0 group-hover:opacity-100"} transition-opacity`}
                      >
                        {isCover ? "Cover" : "Set cover"}
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  data-testid="admin-upload-button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-20 h-24 border border-dashed border-white/20 flex flex-col items-center justify-center gap-2 text-zinc-500 hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  <Upload size={16} />
                  <span className="text-[9px] tracking-[0.2em] uppercase">{uploading ? "..." : "Upload"}</span>
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="admin-file-input" />
              </div>
            </div>

            <label className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-zinc-400 md:col-span-2">
              <input
                type="checkbox"
                data-testid="admin-form-featured"
                checked={form.featured}
                onChange={(e) => set("featured", e.target.checked)}
                className="w-4 h-4 accent-[#D4AF37]"
              />
              Featured on homepage
            </label>

            <button
              data-testid="admin-form-submit"
              type="submit"
              className="md:col-span-2 py-4 bg-gold text-black text-xs tracking-[0.35em] uppercase font-semibold hover:bg-gold-hover transition-colors duration-500"
            >
              {editing ? "Update Piece" : "Add to Vault"}
            </button>
          </form>
        )}

        {tab === "products" && (
          <div className="mt-10" data-testid="admin-products-list">
            {products.length === 0 ? (
              <div className="border border-white/5 py-16 text-center">
                <p className="font-serif text-2xl text-zinc-400 italic">The vault is empty</p>
                <p className="mt-3 text-sm text-zinc-600">Add your first piece above.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {products.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`admin-product-row-${p.id}`}
                    className="border border-white/10 bg-[#141414] px-6 py-4 flex items-center gap-6"
                  >
                    <div className="w-12 h-14 bg-[#0A0A0A] border border-white/10 overflow-hidden flex-shrink-0">
                      {p.images?.[0] && <img src={fileUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-lg text-[#F5F5F5] truncate">{p.name}</p>
                      <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 truncate">
                        {p.partner} &mdash; {p.category}
                        {p.featured ? " \u00B7 featured" : ""}
                      </p>
                    </div>
                    <p className="text-sm text-gold whitespace-nowrap">{eur(p.price)}</p>
                    <div className="flex gap-2">
                      <button
                        data-testid={`admin-edit-${p.id}`}
                        onClick={() => startEdit(p)}
                        className="p-2 border border-white/10 text-zinc-400 hover:border-gold hover:text-gold transition-colors"
                        aria-label="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        data-testid={`admin-delete-${p.id}`}
                        onClick={() => remove(p.id)}
                        className="p-2 border border-white/10 text-zinc-400 hover:border-red-400 hover:text-red-400 transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-10" data-testid="admin-orders-list">
            {orders.length === 0 ? (
              <div className="border border-white/5 py-16 text-center">
                <p className="font-serif text-2xl text-zinc-400 italic">No orders yet</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {orders.map((o) => (
                  <div key={o.id} className="border border-white/10 bg-[#141414] px-6 py-5" data-testid={`admin-order-${o.id}`}>
                    <div className="flex justify-between flex-wrap gap-3">
                      <div>
                        <p className="text-xs tracking-[0.3em] uppercase text-gold">{o.id.slice(0, 8).toUpperCase()}</p>
                        <p className="text-xs text-zinc-500 mt-1">
                          {o.email} &mdash; {o.shipping?.city}, {o.shipping?.country}
                        </p>
                      </div>
                      <p className="font-serif text-lg text-gold">{eur(o.total)}</p>
                    </div>
                    <div className="mt-3 text-sm text-zinc-400">
                      {o.items.map((i, idx) => (
                        <span key={idx}>
                          {i.name} ({i.size} &times;{i.qty}){idx < o.items.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
